import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CampaignIcon from '@mui/icons-material/Campaign';
import { Announcement, AnnouncementCategory } from '../ts/Common';

function getCategoryColors(category: AnnouncementCategory): { bg: string; border: string; text: string } {
    switch (category) {
        case AnnouncementCategory.Info:      return { bg: '#e8f4fd', border: '#0288d1', text: '#01579b' };
        case AnnouncementCategory.Success:   return { bg: '#edf7ed', border: '#2e7d32', text: '#1b5e20' };
        case AnnouncementCategory.Warning:   return { bg: '#fff8e1', border: '#ed6c02', text: '#7c4500' };
        case AnnouncementCategory.Danger:    return { bg: '#fde8e8', border: '#d32f2f', text: '#7f1d1d' };
        case AnnouncementCategory.Primary:   return { bg: '#e8eaf6', border: '#00004B', text: '#00004B' };
        case AnnouncementCategory.Secondary: return { bg: '#fff3e0', border: '#F7914B', text: '#7c3900' };
        default:                             return { bg: '#e8f4fd', border: '#0288d1', text: '#01579b' };
    }
}

function annDismissKey(announcement: Announcement): string {
    return announcement.announcementId != null
        ? `id:${announcement.announcementId}`
        : `fallback:${announcement.category}-${announcement.title}-${announcement.description}`;
}

const DISMISSED_KEY = 'spectre_dismissed_announcements';

function getDismissedKeys(): Set<string> {
    try {
        const raw = localStorage.getItem(DISMISSED_KEY);
        return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
        return new Set();
    }
}

function persistDismissedKeys(keys: Set<string>): void {
    try {
        localStorage.setItem(DISMISSED_KEY, JSON.stringify([...keys]));
    } catch {
        // ignore storage errors (e.g., quota/blocked storage)
    }
}

interface AnnouncementBannerProps {
    announcements: Announcement[];
    offsetTop?: number;
}

export function AnnouncementBanner({ announcements, offsetTop = 64 }: AnnouncementBannerProps) {
    const [dismissed, setDismissed] = useState<Set<string>>(() => getDismissedKeys());

    const visible = announcements.filter(ann => !dismissed.has(annDismissKey(ann)));
    if (visible.length === 0) return null;

    const dismiss = (announcement: Announcement) => {
        setDismissed(prev => {
            const next = new Set([...prev, annDismissKey(announcement)]);
            persistDismissedKeys(next);
            return next;
        });
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: offsetTop,
                left: 0,
                right: 0,
                zIndex: 1200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
                px: 1,
                pt: 1,
                gap: 0.75,
            }}
        >
            {visible.map((announcement) => {
                const colors = getCategoryColors(announcement.category);
                return (
                    <Box
                        key={annDismissKey(announcement)}
                        sx={{
                            pointerEvents: 'auto',
                            width: '100%',
                            maxWidth: 680,
                            bgcolor: colors.bg,
                            borderLeft: `4px solid ${colors.border}`,
                            borderRadius: 1,
                            boxShadow: 2,
                            display: 'flex',
                            alignItems: 'flex-start',
                            px: 1.5,
                            py: 1,
                            gap: 1,
                        }}
                    >
                        <CampaignIcon sx={{ color: colors.border, flexShrink: 0, mt: 0.25 }} fontSize="small" />
                        <Typography sx={{ flex: 1, fontSize: '0.85rem', color: colors.text }}>
                            <strong>{announcement.title}</strong>: {announcement.description}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => dismiss(announcement)}
                            sx={{ color: colors.text, flexShrink: 0, p: 0.25, ml: 0.5 }}
                            aria-label={`Dismiss announcement: ${announcement.title}`}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}
        </Box>
    );
}
